// useSaved — read-path hook for the Saved view.
//
// The list lives in `MatrixContext.savedItems` and is sorted newest first here.

import { useMemo } from "react";

import { useMatrix } from "~/context/MatrixContext";
import { useRooms } from "~/hooks/useRooms";
import type { SavedItem } from "~/lib/matrix";

export interface UseSavedResult {
  /** Saved items, newest first. */
  items: SavedItem[];
  /** Constant-time check used by MessageActions to toggle the glyph. */
  isSaved: (eventId: string) => boolean;
}

export function useSaved(): UseSavedResult {
  const { savedItems } = useMatrix();
  const rooms = useRooms();

  // Compute the sorted list and the O(1) lookup set in one pass so
  // a large saved list doesn't re-iterate for every MessageActions row.
  const { sorted, savedSet } = useMemo(() => {
    const visibleRoomIds = new Set(rooms.map((room) => room.id));
    const set = new Set<string>();
    const sortedItems = savedItems.filter((item) => visibleRoomIds.has(item.roomId));
    for (const item of sortedItems) {
      set.add(item.eventId);
    }
    sortedItems.sort((a, b) => b.savedAt - a.savedAt);
    return { sorted: sortedItems, savedSet: set };
  }, [rooms, savedItems]);

  return {
    items: sorted,
    isSaved: (eventId: string) => savedSet.has(eventId),
  };
}
